import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Form, Button } from "antd";
import { visitorInfoService } from "../../../services/visitorInfo.service";
import IntlMessages from "@iso/components/utility/intlMessages";
import VisitorModalWrapper from "./VisitorModal.styles";
import VisitorDetailsFields from "./VisitorDetailsFields";
import Alert from "@iso/components/Feedback/Alert";
import moment from "moment";
import { VISITOR_INFO_TYPES } from "@iso/constants/visitor";

const VisitorInfoModal = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const currentUser = useSelector((state) => state.user.user);
  const [errMsg, setErrMsg] = useState("");
  // Watch the type radio so the scheduling picker can flip between
  // "time today" (visitor) and "date + time, today or later" (contractor).
  const visitorType = Form.useWatch("visitor_type", form) || VISITOR_INFO_TYPES.VISITOR;

  useEffect(() => {
    setFields([
      {
        name: "name",
        value: props.editedVisitor.name,
      },
      {
        name: "email",
        value: props.editedVisitor.email,
      },
      {
        name: "phone",
        value: props.editedVisitor.phone,
      },
      {
        name: "visitor_type",
        // Default new records to Visitor; edits keep their stored type.
        value: props.editedVisitor.visitor_type || VISITOR_INFO_TYPES.VISITOR,
      },
      {
        name: "business_name",
        value: props.editedVisitor.business_name,
      },
      {
        name: "scheduled_arrival",
        // Default to now — the spec's "visitor defaults to current time".
        value: props.editedVisitor.scheduled_arrival
          ? moment(props.editedVisitor.scheduled_arrival)
          : moment(),
      },
      {
        name: "emergency_name",
        value: props.editedVisitor.emergency_name,
      },
      {
        name: "emergency_contact",
        value: props.editedVisitor.emergency_contact,
      },
      {
        name: "emergency_mobile_number",
        value: props.editedVisitor.emergency_mobile_number,
      },
      {
        name: "birthday",
        value: props.editedVisitor.birthday
          ? moment(props.editedVisitor.birthday)
          : "",
      },
    ]);
  }, [props]);

  const cancelModal = () => {
    props.cancelModal();
    resetForm();
  };

  const handleSubmit = (e) => {
    setLoading(true);
    form
      .validateFields()
      .then(async (values) => {
        values.property_id = props.editedVisitor.property_id
          ? props.editedVisitor.property_id
          : props.propertyId;

        // Serialise the scheduling moment for the API. Visitors pick a
        // time-of-day only — pin it to today's date so the payload can
        // never claim another day (the backend enforces the same rule).
        if (values.scheduled_arrival) {
          const m =
            values.visitor_type === VISITOR_INFO_TYPES.VISITOR
              ? moment().set({
                  hour: values.scheduled_arrival.hour(),
                  minute: values.scheduled_arrival.minute(),
                  second: 0,
                })
              : values.scheduled_arrival;
          values.scheduled_arrival = m.format("YYYY-MM-DD HH:mm:ss");
        }

        onStore(values);
      })
      .catch((info) => {
        setLoading(false);
      });
  };

  const onStore = (data) => {
    const params = {
        'visitor_info': {...data}
    }
    if(props.editedVisitor.id){
        handleUpdate(params,props.editedVisitor.id);
    }else{
        handleCreate(params);
    }
  
  };
  const handleCreate = (data) => {
    visitorInfoService
      .create(data)
      .then((res) => {
        if (res?.code === "0000") {
          props.updateVisitorData();
          props.cancelModal();
          resetForm();
        } else {
          // Server-side rejection (e.g. the visitor/contractor
          // scheduling-window rule) — show the message and re-enable
          // Save so the user can correct and resubmit.
          setErrMsg(res?.message || "Failed to save visitor.");
          setLoading(false);
        }
      })
      .catch(() => {
        setErrMsg("Failed to save visitor. Please try again.");
        setLoading(false);
      });
  };

  const handleUpdate = (data, id) => {
    visitorInfoService
      .update(data, id)
      .then((res) => {
        if (res?.code === "0000") {
          props.updateVisitorData();
          props.cancelModal();
          resetForm();
        } else {
          setErrMsg(res?.message || "Failed to save visitor.");
          setLoading(false);
        }
      })
      .catch(() => {
        setErrMsg("Failed to save visitor. Please try again.");
        setLoading(false);
      });
  };

  const resetForm = () => {
    setTimeout(() => {
      form.resetFields();
      setLoading(false);
      setErrMsg("");
    }, 500);
  };

  return (
    <VisitorModalWrapper
      className="visitor-modal"
      forceRender={true}
      open={props.visibleVisitorModal}
      title={<IntlMessages id="visitorPage.modal.visitorDetail" />}
      onCancel={cancelModal}
      maskClosable={true}
      footer={[
        <Button key="back" onClick={cancelModal} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
        <Button
          key="submit"
          className="btn-success"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          {<IntlMessages id="propertyPage.modal.save" />}
        </Button>,
      ]}
    >
      <Form
        form={form}
        name="addVisitorInfo"
        layout="vertical"
        scrollToFirstError
        fields={fields}
      >
        {errMsg && (
          <Alert
            message={errMsg}
            type="error"
            style={{ margin: "15px 15px 0 15px" }}
          />
        )}
        <div className="form-body">
          {/* Shared with the Run Induction flow's details step — see
              VisitorDetailsFields. Editing the layout there changes both. */}
          <VisitorDetailsFields form={form} visitorType={visitorType} />
        </div>
      </Form>
    </VisitorModalWrapper>
  );
};

export default VisitorInfoModal;
